import { useEffect, useState } from 'react';
import { AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react';
import { fmtTs } from '../lib/globalTz';

type BannerSeverity = 'info' | 'success' | 'warning' | 'error';

interface BannerNotification {
  id: string;
  title: string;
  message: string;
  severity: BannerSeverity;
  startsAt?: string;
  endsAt?: string | null;
  createdAt?: string;
}

function severityClasses(severity: BannerSeverity): string {
  switch (severity) {
    case 'error':
      return 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200';
    case 'warning':
      return 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200';
    case 'success':
      return 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200';
    case 'info':
    default:
      return 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200';
  }
}

function Icon({ severity }: { severity: BannerSeverity }) {
  if (severity === 'error') return <XCircle size={18} />;
  if (severity === 'warning') return <AlertTriangle size={18} />;
  if (severity === 'success') return <CheckCircle size={18} />;
  return <Info size={18} />;
}

export function BannerNotifications() {
  const [banners, setBanners] = useState<BannerNotification[]>([]);

  async function loadBanners() {
    try {
      const resp = await fetch('/api/banner-notifications/active', {
        credentials: 'include',
      });

      if (!resp.ok) return;

      const data = await resp.json() as { banners?: BannerNotification[] };
      setBanners(data.banners ?? []);
    } catch {
      setBanners([]);
    }
  }

  useEffect(() => {
    void loadBanners();

    const timer = window.setInterval(() => {
      void loadBanners();
    }, 60_000);

    return () => window.clearInterval(timer);
  }, []);

  if (banners.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      {banners.map((b) => (
        <div
          key={b.id}
          className={`rounded-xl border px-4 py-3 shadow-sm flex gap-3 items-start ${severityClasses(b.severity)}`}
        >
          <div className="mt-0.5 flex-shrink-0">
            <Icon severity={b.severity} />
          </div>
          <div className="min-w-0">
            <div className="font-bold text-sm">{b.title}</div>
            <div className="text-sm mt-0.5 whitespace-pre-wrap">{b.message}</div>
            {b.endsAt && (
              <div className="text-[11px] opacity-75 mt-1">
                Active until {fmtTs(b.endsAt)}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
